import React, { useState, useEffect, useCallback } from "react";
import api from "@/services/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  Plus,
  Receipt,
  TrendingDown,
  TrendingUp,
  DollarSign,
  ShoppingCart,
  Truck,
  Lightbulb,
  MoreHorizontal,
  Calendar,
  Search,
  Edit,
  XCircle,
  FileText,
  Building2,
  CreditCard,
  Download,
  Filter,
  ChevronDown,
  ChevronUp,
  AlertTriangle,
  CheckCircle,
  Clock,
  Home,
  Wrench,
  Megaphone,
  Calculator,
  Scale,
  Banknote,
} from "lucide-react";
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip,
} from "recharts";

const CATEGORIA_ICONS = {
  insumos: ShoppingCart,
  transporte: Truck,
  servicios: Lightbulb,
  nomina: DollarSign,
  arriendo: Home,
  mantenimiento: Wrench,
  marketing: Megaphone,
  equipos: Calculator,
  impuestos: Scale,
  financieros: Banknote,
  legales: FileText,
  otros: MoreHorizontal,
};

const CATEGORIA_COLORS = {
  insumos: "#8B4513",
  transporte: "#3B82F6",
  servicios: "#F59E0B",
  nomina: "#10B981",
  arriendo: "#6366F1",
  mantenimiento: "#EC4899",
  marketing: "#F97316",
  equipos: "#14B8A6",
  impuestos: "#EF4444",
  financieros: "#8B5CF6",
  legales: "#64748B",
  otros: "#6B7280",
};

const ESTADO_PAGO_ICONS = {
  pagado: CheckCircle,
  pendiente: Clock,
  parcial: AlertTriangle,
  anulado: XCircle,
};

const ESTADO_PAGO_COLORS = {
  pagado: "text-green-600 bg-green-50",
  pendiente: "text-amber-600 bg-amber-50",
  parcial: "text-orange-600 bg-orange-50",
  anulado: "text-red-600 bg-red-50",
};

export default function AdminGastos() {
  const [gastos, setGastos] = useState([]);
  const [resumen, setResumen] = useState(null);
  const [enums, setEnums] = useState(null);
  const [proveedores, setProveedores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [showAnulados, setShowAnulados] = useState(false);
  
  // Filters
  const [filtroCategoria, setFiltroCategoria] = useState("todas");
  const [filtroEstadoPago, setFiltroEstadoPago] = useState("todos");
  const [filtroFechaInicio, setFiltroFechaInicio] = useState("");
  const [filtroFechaFin, setFiltroFechaFin] = useState("");
  const [search, setSearch] = useState("");
  
  // Form
  const [form, setForm] = useState({
    descripcion: "",
    monto: "",
    categoria: "insumos",
    fecha_gasto: new Date().toISOString().split("T")[0],
    // Proveedor
    proveedor_razon_social: "",
    proveedor_tipo_documento: "nit",
    proveedor_numero_documento: "",
    proveedor_telefono: "",
    proveedor_email: "",
    proveedor_direccion: "",
    proveedor_ciudad: "",
    // Documento
    documento_tipo: "factura_electronica",
    documento_numero: "",
    documento_fecha: new Date().toISOString().split("T")[0],
    // Pago
    estado_pago: "pagado",
    metodo_pago: "efectivo",
    fecha_pago: "",
    // Contable
    centro_costo: "",
    cuenta_contable: "",
    // Impuestos
    base_gravable: "",
    iva: "",
    retencion_fuente: "",
    retencion_ica: "",
    // Notas
    notas: "",
  });

  // Dialog for voiding
  const [anularDialog, setAnularDialog] = useState({ open: false, gasto: null, motivo: "" });

  useEffect(() => {
    fetchEnums();
  }, []);

  useEffect(() => {
    fetchData();
  }, [filtroCategoria, filtroEstadoPago, filtroFechaInicio, filtroFechaFin, showAnulados]);

  const fetchEnums = async () => {
    try {
      const res = await api.get("/gastos/enums");
      setEnums(res.data);
    } catch (error) {
      console.error("Error fetching enums:", error);
    }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filtroCategoria !== "todas") params.categoria = filtroCategoria;
      if (filtroEstadoPago !== "todos") params.estado_pago = filtroEstadoPago;
      if (filtroFechaInicio) params.fecha_inicio = filtroFechaInicio;
      if (filtroFechaFin) params.fecha_fin = filtroFechaFin;
      params.incluir_anulados = showAnulados;

      const [gastosRes, resumenRes, proveedoresRes] = await Promise.all([
        api.get("/gastos", { params }),
        api.get("/gastos/resumen", { params: { fecha_inicio: filtroFechaInicio, fecha_fin: filtroFechaFin } }),
        api.get("/gastos/proveedores"),
      ]);
      setGastos(gastosRes.data.gastos || []);
      setResumen(resumenRes.data);
      setProveedores(proveedoresRes.data || []);
    } catch (error) {
      console.error("Error fetching gastos:", error);
      toast.error("Error al cargar los datos");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        descripcion: form.descripcion,
        monto: parseFloat(form.monto),
        categoria: form.categoria,
        fecha_gasto: form.fecha_gasto,
        proveedor: {
          razon_social: form.proveedor_razon_social,
          tipo_documento: form.proveedor_tipo_documento,
          numero_documento: form.proveedor_numero_documento,
          telefono: form.proveedor_telefono || null,
          email: form.proveedor_email || null,
          direccion: form.proveedor_direccion || null,
          ciudad: form.proveedor_ciudad || null,
        },
        documento: {
          tipo: form.documento_tipo,
          numero: form.documento_numero,
          fecha_documento: form.documento_fecha,
        },
        estado_pago: form.estado_pago,
        metodo_pago: form.metodo_pago || null,
        fecha_pago: form.fecha_pago || null,
        centro_costo: form.centro_costo || null,
        cuenta_contable: form.cuenta_contable || null,
        base_gravable: form.base_gravable ? parseFloat(form.base_gravable) : null,
        iva: form.iva ? parseFloat(form.iva) : null,
        retencion_fuente: form.retencion_fuente ? parseFloat(form.retencion_fuente) : null,
        retencion_ica: form.retencion_ica ? parseFloat(form.retencion_ica) : null,
        notas: form.notas || null,
      };

      if (editingId) {
        await api.put(`/gastos/${editingId}`, payload);
        toast.success("Gasto actualizado");
      } else {
        const res = await api.post("/gastos", payload);
        toast.success(`Gasto registrado - Consecutivo #${res.data.consecutivo}`);
      }
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al guardar");
    }
  };

  const handleEdit = (gasto) => {
    setEditingId(gasto.id);
    const prov = gasto.proveedor || {};
    const doc = gasto.documento || {};
    setForm({
      descripcion: gasto.descripcion || "",
      monto: gasto.monto?.toString() || "",
      categoria: gasto.categoria || "insumos",
      fecha_gasto: gasto.fecha_gasto || "",
      proveedor_razon_social: prov.razon_social || "",
      proveedor_tipo_documento: prov.tipo_documento || "nit",
      proveedor_numero_documento: prov.numero_documento || "",
      proveedor_telefono: prov.telefono || "",
      proveedor_email: prov.email || "",
      proveedor_direccion: prov.direccion || "",
      proveedor_ciudad: prov.ciudad || "",
      documento_tipo: doc.tipo || "factura_electronica",
      documento_numero: doc.numero || "",
      documento_fecha: doc.fecha_documento || "",
      estado_pago: gasto.estado_pago || "pagado",
      metodo_pago: gasto.metodo_pago || "efectivo",
      fecha_pago: gasto.fecha_pago || "",
      centro_costo: gasto.centro_costo || "",
      cuenta_contable: gasto.cuenta_contable || "",
      base_gravable: gasto.base_gravable?.toString() || "",
      iva: gasto.iva?.toString() || "",
      retencion_fuente: gasto.retencion_fuente?.toString() || "",
      retencion_ica: gasto.retencion_ica?.toString() || "",
      notas: gasto.notas || "",
    });
    setDialogOpen(true);
  };

  const handleAnular = async () => {
    if (!anularDialog.motivo || anularDialog.motivo.length < 5) {
      toast.error("El motivo debe tener al menos 5 caracteres");
      return;
    }
    try {
      await api.post(`/gastos/${anularDialog.gasto.id}/anular?motivo=${encodeURIComponent(anularDialog.motivo)}`);
      toast.success("Gasto anulado correctamente");
      setAnularDialog({ open: false, gasto: null, motivo: "" });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al anular");
    }
  };

  const handleExportar = async (formato) => {
    try {
      const params = {
        fecha_inicio: filtroFechaInicio || new Date().toISOString().slice(0, 7) + "-01",
        fecha_fin: filtroFechaFin || new Date().toISOString().split("T")[0],
        formato
      };
      const res = await api.get("/gastos/exportar", { params });
      
      if (formato === "csv") {
        const blob = new Blob([res.data.contenido], { type: "text/csv" });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = res.data.nombre_archivo;
        a.click();
        toast.success("Archivo exportado");
      } else {
        // JSON download
        const blob = new Blob([JSON.stringify(res.data.gastos, null, 2)], { type: "application/json" });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `gastos_${params.fecha_inicio}_${params.fecha_fin}.json`;
        a.click();
        toast.success("Archivo exportado");
      }
    } catch (error) {
      toast.error("Error al exportar");
    }
  };

  const selectProveedor = (proveedor) => {
    setForm({
      ...form,
      proveedor_razon_social: proveedor.razon_social || "",
      proveedor_tipo_documento: proveedor.tipo_documento || "nit",
      proveedor_numero_documento: proveedor.numero_documento || "",
      proveedor_telefono: proveedor.telefono || "",
      proveedor_email: proveedor.email || "",
      proveedor_direccion: proveedor.direccion || "",
      proveedor_ciudad: proveedor.ciudad || "",
    });
  };

  const resetForm = () => {
    setEditingId(null);
    setForm({
      descripcion: "",
      monto: "",
      categoria: "insumos",
      fecha_gasto: new Date().toISOString().split("T")[0],
      proveedor_razon_social: "",
      proveedor_tipo_documento: "nit",
      proveedor_numero_documento: "",
      proveedor_telefono: "",
      proveedor_email: "",
      proveedor_direccion: "",
      proveedor_ciudad: "",
      documento_tipo: "factura_electronica",
      documento_numero: "",
      documento_fecha: new Date().toISOString().split("T")[0],
      estado_pago: "pagado",
      metodo_pago: "efectivo",
      fecha_pago: "",
      centro_costo: "",
      cuenta_contable: "",
      base_gravable: "",
      iva: "",
      retencion_fuente: "",
      retencion_ica: "",
      notas: "",
    });
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat("es-CO", {
      style: "currency",
      currency: "COP",
      minimumFractionDigits: 0,
    }).format(value || 0);
  };

  const getCategoriaInfo = (id) => {
    const cat = enums?.categorias?.find((c) => c.id === id);
    return {
      nombre: cat?.nombre || id,
      icon: CATEGORIA_ICONS[id] || MoreHorizontal,
      color: CATEGORIA_COLORS[id] || "#6B7280",
    };
  };

  const filtered = gastos.filter(
    (g) =>
      g.descripcion?.toLowerCase().includes(search.toLowerCase()) ||
      g.proveedor?.razon_social?.toLowerCase().includes(search.toLowerCase()) ||
      g.proveedor?.numero_documento?.includes(search) ||
      g.documento?.numero?.toLowerCase().includes(search.toLowerCase())
  );

  // Prepare chart data
  const pieData = Object.entries(resumen?.por_categoria || {}).map(([id, data]) => ({
    name: getCategoriaInfo(id).nombre,
    value: data.total,
    color: CATEGORIA_COLORS[id] || "#6B7280",
  })).filter((d) => d.value > 0);

  if (loading && !gastos.length) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-pulse text-torta-brown">Cargando...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-gastos">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl text-torta-dark">Gestión de Gastos</h1>
          <p className="text-torta-muted">Control contable de costos y compras</p>
        </div>

        <div className="flex gap-2">
          <Select onValueChange={handleExportar}>
            <SelectTrigger className="w-36">
              <Download className="w-4 h-4 mr-2" />
              Exportar
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="csv">Exportar CSV</SelectItem>
              <SelectItem value="json">Exportar JSON</SelectItem>
            </SelectContent>
          </Select>

          <Dialog
            open={dialogOpen}
            onOpenChange={(open) => {
              setDialogOpen(open);
              if (!open) resetForm();
            }}
          >
            <DialogTrigger asChild>
              <Button className="bg-torta-brown hover:bg-torta-brown/90">
                <Plus className="w-4 h-4 mr-2" />
                Registrar Gasto
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="font-display text-xl">
                  {editingId ? "Editar Gasto" : "Nuevo Gasto"}
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-6 mt-4">
                {/* Basic Info */}
                <div className="space-y-4">
                  <h3 className="font-medium text-torta-dark flex items-center gap-2">
                    <Receipt className="w-4 h-4" /> Información del Gasto
                  </h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="col-span-2 space-y-2">
                      <Label>Descripción *</Label>
                      <Input
                        required
                        value={form.descripcion}
                        onChange={(e) => setForm({ ...form, descripcion: e.target.value })}
                        placeholder="Ej: Compra de harina para producción"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Monto Total ($) *</Label>
                      <Input
                        type="number"
                        required
                        min="0"
                        value={form.monto}
                        onChange={(e) => setForm({ ...form, monto: e.target.value })}
                        placeholder="150000"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Categoría *</Label>
                      <Select
                        value={form.categoria}
                        onValueChange={(value) => setForm({ ...form, categoria: value })}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {enums?.categorias?.map((cat) => (
                            <SelectItem key={cat.id} value={cat.id}>
                              {cat.nombre}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Fecha del Gasto *</Label>
                      <Input
                        type="date"
                        required
                        value={form.fecha_gasto}
                        onChange={(e) => setForm({ ...form, fecha_gasto: e.target.value })}
                      />
                    </div>
                  </div>
                </div>

                {/* Proveedor */}
                <div className="space-y-4 border-t pt-4">
                  <div className="flex items-center justify-between">
                    <h3 className="font-medium text-torta-dark flex items-center gap-2">
                      <Building2 className="w-4 h-4" /> Información del Proveedor
                    </h3>
                    {proveedores.length > 0 && (
                      <Select onValueChange={(nit) => {
                        const prov = proveedores.find(p => p.numero_documento === nit);
                        if (prov) selectProveedor(prov);
                      }}>
                        <SelectTrigger className="w-48">
                          <SelectValue placeholder="Proveedores recientes" />
                        </SelectTrigger>
                        <SelectContent>
                          {proveedores.slice(0, 10).map((p) => (
                            <SelectItem key={p.numero_documento} value={p.numero_documento}>
                              {p.razon_social}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="col-span-2 space-y-2">
                      <Label>Razón Social / Nombre *</Label>
                      <Input
                        required
                        value={form.proveedor_razon_social}
                        onChange={(e) => setForm({ ...form, proveedor_razon_social: e.target.value })}
                        placeholder="Distribuidora XYZ S.A.S."
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Tipo de Documento *</Label>
                      <Select
                        value={form.proveedor_tipo_documento}
                        onValueChange={(value) => setForm({ ...form, proveedor_tipo_documento: value })}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {enums?.tipos_documento_proveedor?.map((t) => (
                            <SelectItem key={t.id} value={t.id}>
                              {t.nombre}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Número de Documento (NIT/CC) *</Label>
                      <Input
                        required
                        value={form.proveedor_numero_documento}
                        onChange={(e) => setForm({ ...form, proveedor_numero_documento: e.target.value })}
                        placeholder="900123456-7"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Teléfono</Label>
                      <Input
                        value={form.proveedor_telefono}
                        onChange={(e) => setForm({ ...form, proveedor_telefono: e.target.value })}
                        placeholder="3001234567"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Email</Label>
                      <Input
                        type="email"
                        value={form.proveedor_email}
                        onChange={(e) => setForm({ ...form, proveedor_email: e.target.value })}
                        placeholder="contacto@proveedor.com"
                      />
                    </div>
                  </div>
                </div>

                {/* Documento Soporte */}
                <div className="space-y-4 border-t pt-4">
                  <h3 className="font-medium text-torta-dark flex items-center gap-2">
                    <FileText className="w-4 h-4" /> Documento Soporte
                  </h3>
                  <div className="grid grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label>Tipo de Documento *</Label>
                      <Select
                        value={form.documento_tipo}
                        onValueChange={(value) => setForm({ ...form, documento_tipo: value })}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {enums?.tipos_documento_soporte?.map((t) => (
                            <SelectItem key={t.id} value={t.id}>
                              {t.nombre}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Número de Documento *</Label>
                      <Input
                        required
                        value={form.documento_numero}
                        onChange={(e) => setForm({ ...form, documento_numero: e.target.value })}
                        placeholder="FE-001234"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Fecha del Documento *</Label>
                      <Input
                        type="date"
                        required
                        value={form.documento_fecha}
                        onChange={(e) => setForm({ ...form, documento_fecha: e.target.value })}
                      />
                    </div>
                  </div>
                </div>

                {/* Pago */}
                <div className="space-y-4 border-t pt-4">
                  <h3 className="font-medium text-torta-dark flex items-center gap-2">
                    <CreditCard className="w-4 h-4" /> Información de Pago
                  </h3>
                  <div className="grid grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label>Estado de Pago</Label>
                      <Select
                        value={form.estado_pago}
                        onValueChange={(value) => setForm({ ...form, estado_pago: value })}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {enums?.estados_pago?.map((e) => (
                            <SelectItem key={e.id} value={e.id}>
                              {e.nombre}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Método de Pago</Label>
                      <Select
                        value={form.metodo_pago}
                        onValueChange={(value) => setForm({ ...form, metodo_pago: value })}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {enums?.metodos_pago?.map((m) => (
                            <SelectItem key={m.id} value={m.id}>
                              {m.nombre}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Fecha de Pago</Label>
                      <Input
                        type="date"
                        value={form.fecha_pago}
                        onChange={(e) => setForm({ ...form, fecha_pago: e.target.value })}
                      />
                    </div>
                  </div>
                </div>

                {/* Impuestos */}
                <div className="space-y-4 border-t pt-4">
                  <h3 className="font-medium text-torta-dark flex items-center gap-2">
                    <Scale className="w-4 h-4" /> Información Tributaria (Opcional)
                  </h3>
                  <div className="grid grid-cols-4 gap-4">
                    <div className="space-y-2">
                      <Label>Base Gravable</Label>
                      <Input
                        type="number"
                        value={form.base_gravable}
                        onChange={(e) => setForm({ ...form, base_gravable: e.target.value })}
                        placeholder="0"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>IVA</Label>
                      <Input
                        type="number"
                        value={form.iva}
                        onChange={(e) => setForm({ ...form, iva: e.target.value })}
                        placeholder="0"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Ret. Fuente</Label>
                      <Input
                        type="number"
                        value={form.retencion_fuente}
                        onChange={(e) => setForm({ ...form, retencion_fuente: e.target.value })}
                        placeholder="0"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Ret. ICA</Label>
                      <Input
                        type="number"
                        value={form.retencion_ica}
                        onChange={(e) => setForm({ ...form, retencion_ica: e.target.value })}
                        placeholder="0"
                      />
                    </div>
                  </div>
                </div>

                {/* Notas */}
                <div className="space-y-2 border-t pt-4">
                  <Label>Notas Adicionales</Label>
                  <Textarea
                    value={form.notas}
                    onChange={(e) => setForm({ ...form, notas: e.target.value })}
                    placeholder="Observaciones, detalles adicionales..."
                    rows={2}
                  />
                </div>

                <div className="flex justify-end gap-3 pt-4 border-t">
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                    Cancelar
                  </Button>
                  <Button type="submit" className="bg-torta-brown hover:bg-torta-brown/90">
                    {editingId ? "Guardar Cambios" : "Registrar Gasto"}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-red-50 border border-red-200 rounded-2xl p-5">
          <TrendingDown className="w-8 h-8 text-red-500 mb-3" />
          <p className="text-red-600 text-sm">Gastos del Período</p>
          <p className="font-display text-2xl text-red-700">
            {formatCurrency(resumen?.total)}
          </p>
          <p className="text-xs text-red-500 mt-1">{resumen?.cantidad || 0} registros</p>
        </div>

        <div className="bg-green-50 border border-green-200 rounded-2xl p-5">
          <TrendingUp className="w-8 h-8 text-green-500 mb-3" />
          <p className="text-green-600 text-sm">Ventas del Período</p>
          <p className="font-display text-2xl text-green-700">
            {formatCurrency(resumen?.ventas_mes)}
          </p>
        </div>

        <div className="bg-blue-50 border border-blue-200 rounded-2xl p-5">
          <DollarSign className="w-8 h-8 text-blue-500 mb-3" />
          <p className="text-blue-600 text-sm">Margen Bruto</p>
          <p className="font-display text-2xl text-blue-700">
            {formatCurrency(resumen?.margen_bruto)}
          </p>
        </div>

        <div className="bg-amber-50 border border-amber-200 rounded-2xl p-5">
          <Clock className="w-8 h-8 text-amber-500 mb-3" />
          <p className="text-amber-600 text-sm">Pendientes de Pago</p>
          <p className="font-display text-2xl text-amber-700">
            {formatCurrency(resumen?.por_estado_pago?.pendiente?.total || 0)}
          </p>
        </div>
      </div>

      {/* Charts */}
      <div className="grid lg:grid-cols-2 gap-6">
        {/* Pie Chart */}
        <div className="bg-white rounded-2xl p-6 shadow-sm">
          <h2 className="font-display text-xl text-torta-dark mb-4">Gastos por Categoría</h2>
          {pieData.length > 0 ? (
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={pieData}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    outerRadius={80}
                    label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                  >
                    {pieData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(v) => formatCurrency(v)} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <p className="text-center py-8 text-torta-muted">No hay datos para mostrar</p>
          )}
        </div>

        {/* Category Summary */}
        <div className="bg-white rounded-2xl p-6 shadow-sm">
          <h2 className="font-display text-xl text-torta-dark mb-4">Desglose por Categoría</h2>
          <div className="space-y-2 max-h-64 overflow-y-auto">
            {Object.entries(resumen?.por_categoria || {}).map(([id, data]) => {
              const catInfo = getCategoriaInfo(id);
              const Icon = catInfo.icon;
              return (
                <div
                  key={id}
                  className="flex items-center justify-between p-3 rounded-xl"
                  style={{ backgroundColor: `${catInfo.color}15` }}
                >
                  <div className="flex items-center gap-3">
                    <div
                      className="w-10 h-10 rounded-lg flex items-center justify-center"
                      style={{ backgroundColor: `${catInfo.color}30` }}
                    >
                      <Icon className="w-5 h-5" style={{ color: catInfo.color }} />
                    </div>
                    <div>
                      <span className="font-medium text-torta-dark">{catInfo.nombre}</span>
                      <p className="text-xs text-torta-muted">{data.cantidad} gastos</p>
                    </div>
                  </div>
                  <span className="font-display text-lg" style={{ color: catInfo.color }}>
                    {formatCurrency(data.total)}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-2xl p-4 shadow-sm space-y-4">
        <div className="flex items-center gap-2 text-torta-dark font-medium">
          <Filter className="w-4 h-4" /> Filtros
        </div>
        <div className="flex flex-wrap gap-4">
          <div className="relative flex-1 min-w-[200px] max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-torta-muted" />
            <Input
              placeholder="Buscar por descripción, proveedor, NIT o # documento..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10"
            />
          </div>
          <Select value={filtroCategoria} onValueChange={setFiltroCategoria}>
            <SelectTrigger className="w-48">
              <SelectValue placeholder="Categoría" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="todas">Todas las categorías</SelectItem>
              {enums?.categorias?.map((cat) => (
                <SelectItem key={cat.id} value={cat.id}>
                  {cat.nombre}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={filtroEstadoPago} onValueChange={setFiltroEstadoPago}>
            <SelectTrigger className="w-40">
              <SelectValue placeholder="Estado" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="todos">Todos los estados</SelectItem>
              {enums?.estados_pago?.map((e) => (
                <SelectItem key={e.id} value={e.id}>
                  {e.nombre}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Input
            type="date"
            value={filtroFechaInicio}
            onChange={(e) => setFiltroFechaInicio(e.target.value)}
            className="w-40"
            placeholder="Desde"
          />
          <Input
            type="date"
            value={filtroFechaFin}
            onChange={(e) => setFiltroFechaFin(e.target.value)}
            className="w-40"
            placeholder="Hasta"
          />
          <div className="flex items-center gap-2">
            <Switch
              checked={showAnulados}
              onCheckedChange={setShowAnulados}
            />
            <span className="text-sm text-torta-muted">Mostrar anulados</span>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-torta-sand/50">
              <tr>
                <th className="text-left p-4 text-sm font-medium text-torta-dark">#</th>
                <th className="text-left p-4 text-sm font-medium text-torta-dark">Fecha</th>
                <th className="text-left p-4 text-sm font-medium text-torta-dark">Proveedor</th>
                <th className="text-left p-4 text-sm font-medium text-torta-dark">Descripción</th>
                <th className="text-left p-4 text-sm font-medium text-torta-dark">Categoría</th>
                <th className="text-left p-4 text-sm font-medium text-torta-dark">Documento</th>
                <th className="text-left p-4 text-sm font-medium text-torta-dark">Estado</th>
                <th className="text-right p-4 text-sm font-medium text-torta-dark">Monto</th>
                <th className="text-right p-4 text-sm font-medium text-torta-dark">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-torta-sand/50">
              {filtered.map((gasto) => {
                const catInfo = getCategoriaInfo(gasto.categoria);
                const Icon = catInfo.icon;
                const EstadoIcon = ESTADO_PAGO_ICONS[gasto.estado_pago] || Clock;
                const isExpanded = expandedId === gasto.id;
                
                return (
                  <React.Fragment key={gasto.id}>
                    <tr 
                      className={`hover:bg-torta-cream/30 cursor-pointer ${gasto.anulado ? 'opacity-50 bg-red-50' : ''}`}
                      onClick={() => setExpandedId(isExpanded ? null : gasto.id)}
                    >
                      <td className="p-4">
                        <span className="text-xs text-torta-muted">#{gasto.consecutivo || '-'}</span>
                      </td>
                      <td className="p-4">
                        <div className="flex items-center gap-2">
                          <Calendar className="w-4 h-4 text-torta-muted" />
                          <span className="text-sm text-torta-dark">
                            {new Date(gasto.fecha_gasto).toLocaleDateString("es-CO")}
                          </span>
                        </div>
                      </td>
                      <td className="p-4">
                        <div>
                          <p className="font-medium text-torta-dark text-sm">{gasto.proveedor?.razon_social || "-"}</p>
                          <p className="text-xs text-torta-muted">
                            {gasto.proveedor?.tipo_documento?.toUpperCase()}: {gasto.proveedor?.numero_documento}
                          </p>
                        </div>
                      </td>
                      <td className="p-4">
                        <p className="font-medium text-torta-dark">{gasto.descripcion}</p>
                        {gasto.anulado && (
                          <span className="text-xs text-red-600 font-medium">ANULADO</span>
                        )}
                      </td>
                      <td className="p-4">
                        <span
                          className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium"
                          style={{
                            backgroundColor: `${catInfo.color}20`,
                            color: catInfo.color,
                          }}
                        >
                          <Icon className="w-3 h-3" />
                          {catInfo.nombre}
                        </span>
                      </td>
                      <td className="p-4">
                        <div className="text-sm">
                          <p className="text-torta-dark">{gasto.documento?.numero || "-"}</p>
                          <p className="text-xs text-torta-muted capitalize">
                            {gasto.documento?.tipo?.replace(/_/g, " ")}
                          </p>
                        </div>
                      </td>
                      <td className="p-4">
                        <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${ESTADO_PAGO_COLORS[gasto.estado_pago]}`}>
                          <EstadoIcon className="w-3 h-3" />
                          {gasto.estado_pago}
                        </span>
                      </td>
                      <td className="p-4 text-right font-medium text-red-600">
                        {formatCurrency(gasto.monto)}
                      </td>
                      <td className="p-4">
                        <div className="flex items-center justify-end gap-1">
                          {!gasto.anulado && (
                            <>
                              <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); handleEdit(gasto); }}>
                                <Edit className="w-4 h-4" />
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={(e) => { e.stopPropagation(); setAnularDialog({ open: true, gasto, motivo: "" }); }}
                                className="text-red-600"
                              >
                                <XCircle className="w-4 h-4" />
                              </Button>
                            </>
                          )}
                          {isExpanded ? <ChevronUp className="w-4 h-4 text-torta-muted" /> : <ChevronDown className="w-4 h-4 text-torta-muted" />}
                        </div>
                      </td>
                    </tr>
                    {/* Expanded Details */}
                    {isExpanded && (
                      <tr className="bg-torta-cream/30">
                        <td colSpan={9} className="p-4">
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                            <div>
                              <p className="text-torta-muted">Método de Pago</p>
                              <p className="font-medium capitalize">{gasto.metodo_pago?.replace(/_/g, " ") || "-"}</p>
                            </div>
                            <div>
                              <p className="text-torta-muted">Fecha de Pago</p>
                              <p className="font-medium">{gasto.fecha_pago ? new Date(gasto.fecha_pago).toLocaleDateString("es-CO") : "-"}</p>
                            </div>
                            <div>
                              <p className="text-torta-muted">Base Gravable</p>
                              <p className="font-medium">{formatCurrency(gasto.base_gravable)}</p>
                            </div>
                            <div>
                              <p className="text-torta-muted">IVA</p>
                              <p className="font-medium">{formatCurrency(gasto.iva)}</p>
                            </div>
                            <div>
                              <p className="text-torta-muted">Ret. Fuente</p>
                              <p className="font-medium">{formatCurrency(gasto.retencion_fuente)}</p>
                            </div>
                            <div>
                              <p className="text-torta-muted">Ret. ICA</p>
                              <p className="font-medium">{formatCurrency(gasto.retencion_ica)}</p>
                            </div>
                            <div>
                              <p className="text-torta-muted">Registrado por</p>
                              <p className="font-medium">{gasto.usuario_nombre}</p>
                            </div>
                            <div>
                              <p className="text-torta-muted">Fecha Registro</p>
                              <p className="font-medium">{new Date(gasto.fecha_registro).toLocaleString("es-CO")}</p>
                            </div>
                            {gasto.notas && (
                              <div className="col-span-2 md:col-span-4">
                                <p className="text-torta-muted">Notas</p>
                                <p className="font-medium">{gasto.notas}</p>
                              </div>
                            )}
                            {gasto.anulado && (
                              <div className="col-span-2 md:col-span-4 bg-red-100 p-3 rounded-lg">
                                <p className="text-red-600 font-medium">Motivo de Anulación:</p>
                                <p className="text-red-800">{gasto.motivo_anulacion}</p>
                                <p className="text-xs text-red-500 mt-1">
                                  Anulado por {gasto.anulado_por_nombre} el {new Date(gasto.fecha_anulacion).toLocaleString("es-CO")}
                                </p>
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
        {filtered.length === 0 && (
          <p className="text-center py-8 text-torta-muted">No se encontraron gastos</p>
        )}
      </div>

      {/* Anular Dialog */}
      <Dialog open={anularDialog.open} onOpenChange={(open) => setAnularDialog({ ...anularDialog, open })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-display text-xl text-red-600">Anular Gasto</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-red-800">
                ¿Está seguro de anular el gasto <strong>#{anularDialog.gasto?.consecutivo}</strong>?
              </p>
              <p className="text-sm text-red-600 mt-2">
                Esta acción no se puede deshacer. El gasto quedará marcado como anulado pero no se eliminará del sistema.
              </p>
            </div>
            <div className="space-y-2">
              <Label>Motivo de Anulación *</Label>
              <Textarea
                required
                value={anularDialog.motivo}
                onChange={(e) => setAnularDialog({ ...anularDialog, motivo: e.target.value })}
                placeholder="Explique el motivo de la anulación..."
                rows={3}
              />
            </div>
            <div className="flex justify-end gap-3">
              <Button variant="outline" onClick={() => setAnularDialog({ open: false, gasto: null, motivo: "" })}>
                Cancelar
              </Button>
              <Button variant="destructive" onClick={handleAnular}>
                Confirmar Anulación
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
